import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  FileCheck2,
  FileUp,
  Languages,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
  UserRoundCog,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { getOrganizationTranslation } from "../context/organizationOnboardingTranslations";
import CertificateTemplateDesigner from "../components/CertificateTemplateDesigner";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const slugify = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const OrganizationSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const token = searchParams.get("token") || "";
  const tr = (key, fallback) =>
    t?.ui?.organizationSetup?.[key] ||
    t?.[key] ||
    getOrganizationTranslation(lang, "organizationSetup", key, fallback);

  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [showDeletionCode, setShowDeletionCode] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateLayout, setTemplateLayout] = useState(null);
  const templateLayoutRef = useRef(null);
  const [slugEdited, setSlugEdited] = useState(false);
  const [form, setForm] = useState({
    adminName: "",
    email: "",
    organizationName: "",
    slug: "",
    defaultLanguage: "fr",
    password: "",
    confirmPassword: "",
    accessCode: "",
    confirmAccessCode: "",
    deletionCode: "",
    confirmDeletionCode: "",
  });

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setMessageType("error");
        setMessage(tr("invitationError", "Invitation invalide, expirée ou déjà utilisée."));
        setChecking(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/auth/organization-invitation/${encodeURIComponent(token)}`
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || tr("invitationError", "Invitation invalide."));
        }

        const organizationName = data.requestedOrganizationName || "";
        setInvitation(data);
        setForm((current) => ({
          ...current,
          adminName: data.name || "",
          email: data.email || "",
          organizationName,
          slug: slugify(organizationName),
          defaultLanguage: lang === "en" ? "en" : "fr",
        }));
      } catch (error) {
        setMessageType("error");
        setMessage(error.message || tr("invitationError", "Invitation invalide."));
      } finally {
        setChecking(false);
      }
    };

    loadInvitation();
  }, [token, lang]);

  const passwordRequirements = useMemo(
    () => [
      { label: lang === "en" ? "At least 8 characters" : "8 caractères minimum", valid: form.password.length >= 8 },
      { label: lang === "en" ? "One uppercase letter" : "Une lettre majuscule", valid: /[A-Z]/.test(form.password) },
      { label: lang === "en" ? "One lowercase letter" : "Une lettre minuscule", valid: /[a-z]/.test(form.password) },
      { label: lang === "en" ? "One number" : "Un chiffre", valid: /\d/.test(form.password) },
    ],
    [form.password, lang]
  );
  const passwordIsValid = passwordRequirements.every((item) => item.valid);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "organizationName" && !slugEdited) {
        next.slug = slugify(value);
      }
      return next;
    });
    setMessage("");
  };

  const handleTemplateLayoutChange = (nextLayout) => {
    templateLayoutRef.current = nextLayout;
    setTemplateLayout(nextLayout);
    setMessage("");
  };

  const resetTemplateLayout = () => {
    templateLayoutRef.current = null;
    setTemplateLayout(null);
  };

  const handleFile = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setTemplateFile(null);
      resetTemplateLayout();
      return;
    }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setMessageType("error");
      setMessage(tr("invalidPdf", "Le fichier sélectionné doit être un PDF."));
      event.target.value = "";
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setMessageType("error");
      setMessage(tr("pdfTooLarge", "Le fichier PDF ne doit pas dépasser 15 Mo."));
      event.target.value = "";
      return;
    }
    setTemplateFile(file);
    resetTemplateLayout();
    setMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setMessageType("");

    if (!passwordIsValid) {
      setMessageType("error");
      setMessage(tr("invalidPassword", "Le mot de passe ne respecte pas les critères de sécurité."));
      return;
    }
    if (form.password !== form.confirmPassword) {
      setMessageType("error");
      setMessage(tr("passwordMismatch", "Les deux mots de passe ne correspondent pas."));
      return;
    }
    if (form.accessCode.trim().length < 6 || form.deletionCode.trim().length < 6) {
      setMessageType("error");
      setMessage(tr("securityCodeLength", "Les codes d’accès et de suppression doivent contenir au moins 6 caractères."));
      return;
    }
    if (form.accessCode !== form.confirmAccessCode) {
      setMessageType("error");
      setMessage(tr("accessCodeMismatch", "Les deux codes d’accès ne correspondent pas."));
      return;
    }
    if (form.deletionCode !== form.confirmDeletionCode) {
      setMessageType("error");
      setMessage(tr("deletionCodeMismatch", "Les deux codes de suppression ne correspondent pas."));
      return;
    }
    if (form.accessCode === form.deletionCode) {
      setMessageType("error");
      setMessage(tr("codesMustDiffer", "Le code d’accès et le code de suppression doivent être différents."));
      return;
    }
    if (!templateFile) {
      setMessageType("error");
      setMessage(tr("pdfRequired", "Veuillez sélectionner un fichier PDF vierge."));
      return;
    }
    const currentTemplateLayout =
      templateLayoutRef.current || templateLayout;

    if (!currentTemplateLayout?.configured) {
      setMessageType("error");
      setMessage(
        tr(
          "layoutRequired",
          "Positionnez les champs sur le PDF puis cliquez sur « Valider la mise en page »."
        )
      );
      return;
    }

    const validatedTemplateLayout = {
      ...currentTemplateLayout,
      configured: true,
      validatedAt: new Date().toISOString(),
    };

    // Synchronise l'état React avant l'envoi et évite une ancienne valeur
    // capturée par le gestionnaire du formulaire.
    templateLayoutRef.current = validatedTemplateLayout;
    setTemplateLayout(validatedTemplateLayout);

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("token", token);
      payload.append("adminName", form.adminName.trim());
      payload.append("email", form.email.trim().toLowerCase());
      payload.append("password", form.password);
      payload.append("accessCode", form.accessCode.trim());
      payload.append("deletionCode", form.deletionCode.trim());
      payload.append("organizationName", form.organizationName.trim());
      payload.append("slug", slugify(form.slug || form.organizationName));
      payload.append("defaultLanguage", form.defaultLanguage);
      payload.append("certificateTemplate", templateFile);
      payload.append(
        "templateLayout",
        JSON.stringify(validatedTemplateLayout)
      );
      payload.append("templateLayoutValidated", "true");

      const response = await fetch(`${API_URL}/api/auth/register-organization`, {
        method: "POST",
        body: payload,
      });

      const rawResponse = await response.text();
      let data = {};

      try {
        data = rawResponse ? JSON.parse(rawResponse) : {};
      } catch {
        data = {
          message:
            rawResponse ||
            `Erreur HTTP ${response.status}`,
        };
      }

      console.log("register-organization response:", {
        status: response.status,
        ok: response.ok,
        data,
      });

      if (!response.ok) {
        console.error("Organization registration failed:", {
          status: response.status,
          code: data?.code,
          message: data?.message,
          collection: data?.collection,
          index: data?.index,
          field: data?.field,
          value: data?.value,
        });

        const registrationError = new Error(
          data?.message ||
            `Impossible de créer l’organisation. Erreur ${response.status}.`
        );

        registrationError.status = response.status;
        registrationError.code = data?.code || null;
        registrationError.details = data;

        throw registrationError;
      }

      setMessageType("success");
      setMessage(tr("success", "Organisation créée avec succès. Redirection vers la connexion..."));
      window.setTimeout(() => navigate("/login", { replace: true }), 1400);
    } catch (error) {
      console.error("OrganizationSetup submit error:", {
        message: error?.message,
        status: error?.status,
        code: error?.code,
        details: error?.details,
      });

      setMessageType("error");
      setMessage(
        error?.message ||
          tr("setupError", "Impossible de créer l’organisation.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-5 text-sm font-bold text-slate-700 shadow-lg">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          {tr("checking", "Vérification de l’invitation...")}
        </div>
      </main>
    );
  }

  if (!invitation) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="max-w-lg rounded-[2rem] border border-rose-200 bg-white p-8 text-center shadow-xl">
          <AlertCircle className="mx-auto h-10 w-10 text-rose-600" />
          <h1 className="mt-4 text-2xl font-black text-slate-950">
            {tr("invitationError", "Invitation invalide, expirée ou déjà utilisée.")}
          </h1>
          {message && <p className="mt-3 text-sm leading-6 text-slate-600">{message}</p>}
        </div>
      </main>
    );
  }

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100";

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
      <div className="mx-auto max-w-[1500px] overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_28px_90px_-40px_rgba(15,23,42,0.5)]">
        <div className="grid lg:grid-cols-[0.82fr_1.18fr]">
          <aside className="relative overflow-hidden bg-slate-950 p-7 text-white sm:p-10 lg:p-12">
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-cyan-500/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-blue-600/20 blur-3xl" />
            <div className="relative flex h-full flex-col">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200">
                <Sparkles size={15} />
                {tr("badge", "Invitation administrateur vérifiée")}
              </div>
              <div className="my-auto py-12">
                <h1 className="text-4xl font-black leading-tight tracking-tight">
                  {tr("heroTitle", "Créez votre environnement CertiLink")}
                </h1>
                <p className="mt-5 text-sm leading-7 text-blue-100">
                  {tr("heroText", "Définissez votre environnement et son modèle PDF.")}
                </p>
                <div className="mt-9 space-y-4">
                  {[
                    [ShieldCheck, tr("secureInvitation", "Lien d’invitation sécurisé")],
                    [Building2, tr("isolatedWorkspace", "Espace et données isolés")],
                    [FileCheck2, tr("oneOfficialTemplate", "Un modèle PDF officiel pour l’organisation")],
                  ].map(([Icon, label]) => (
                    <div key={label} className="flex items-center gap-3 text-sm text-blue-50">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-cyan-300">
                        <Icon className="h-4 w-4" />
                      </span>
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <section className="p-6 sm:p-10 lg:p-12">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
              {tr("formEyebrow", "Création de l’organisation")}
            </p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">
              {tr("formTitle", "Finaliser votre environnement")}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              {tr("formText", "Configurez votre organisation, vos codes de sécurité et son modèle PDF officiel.")}
            </p>

            {message && (
              <div className={`mt-6 flex items-start gap-3 rounded-2xl border p-4 text-sm font-semibold ${
                messageType === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-rose-200 bg-rose-50 text-rose-800"
              }`}>
                {messageType === "success" ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                ) : (
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                )}
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-7">
              <section>
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white">
                    <UserRoundCog className="h-5 w-5" />
                  </span>
                  <h3 className="font-black text-slate-950">{tr("adminIdentity", "Identité de l’administrateur")}</h3>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="mb-2 block text-sm font-bold text-slate-700">{tr("adminName", "Nom complet")} *</span>
                    <input name="adminName" value={form.adminName} onChange={updateField} required className={inputClass} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-2 block text-sm font-bold text-slate-700">{tr("email", "Adresse email")}</span>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input value={form.email} readOnly className={`${inputClass} bg-slate-50 pl-11 text-slate-500`} />
                    </div>
                    <p className="mt-2 text-xs text-slate-500">{tr("lockedEmail", "Adresse vérifiée et non modifiable")}</p>
                  </label>
                </div>
              </section>

              <section className="border-t border-slate-100 pt-7">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cyan-600 text-white">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <h3 className="font-black text-slate-950">{tr("organization", "Organisation")}</h3>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="mb-2 block text-sm font-bold text-slate-700">{tr("organizationName", "Nom de l’organisation")} *</span>
                    <input name="organizationName" value={form.organizationName} onChange={updateField} required className={inputClass} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-bold text-slate-700">{tr("slug", "Identifiant de l’organisation")} *</span>
                    <input
                      name="slug"
                      value={form.slug}
                      onChange={(event) => {
                        setSlugEdited(true);
                        setForm((current) => ({ ...current, slug: slugify(event.target.value) }));
                      }}
                      required
                      className={inputClass}
                    />
                    <p className="mt-2 text-xs text-slate-500">{tr("slugHint", "Utilisé pour les références internes.")}</p>
                  </label>
                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700">
                      <Languages className="h-4 w-4 text-slate-400" />
                      {tr("defaultLanguage", "Langue par défaut")}
                    </span>
                    <select name="defaultLanguage" value={form.defaultLanguage} onChange={updateField} className={inputClass}>
                      <option value="fr">{tr("french", "Français")}</option>
                      <option value="en">{tr("english", "Anglais")}</option>
                    </select>
                  </label>
                </div>
              </section>

              <section className="border-t border-slate-100 pt-7">
                <div className="grid gap-5 sm:grid-cols-2">
                  {[
                    ["password", tr("password", "Mot de passe"), form.password, showPassword, setShowPassword],
                    ["confirmPassword", tr("confirmPassword", "Confirmer le mot de passe"), form.confirmPassword, showConfirmation, setShowConfirmation],
                  ].map(([name, label, value, visible, toggle]) => (
                    <label key={name} className="block">
                      <span className="mb-2 block text-sm font-bold text-slate-700">{label} *</span>
                      <div className="relative">
                        <LockKeyhole className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                          name={name}
                          type={visible ? "text" : "password"}
                          value={value}
                          onChange={updateField}
                          required
                          autoComplete="new-password"
                          className={`${inputClass} pl-11 pr-12`}
                        />
                        <button type="button" onClick={() => toggle((current) => !current)} className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-4 grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                  {passwordRequirements.map((requirement) => (
                    <div key={requirement.label} className={`flex items-center gap-2 text-xs font-bold ${requirement.valid ? "text-emerald-700" : "text-slate-500"}`}>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full ${requirement.valid ? "bg-emerald-100" : "bg-slate-200 text-slate-400"}`}>
                        <Check className="h-3 w-3" />
                      </span>
                      {requirement.label}
                    </div>
                  ))}
                </div>
              </section>



              <section className="border-t border-slate-100 pt-7">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500 text-white">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">
                      {tr("securityCodes", "Codes personnels de sécurité")}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {tr("securityCodesHelp", "Un code pour la connexion administrateur et un autre pour les suppressions.")}
                    </p>
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  {[
                    ["accessCode", tr("accessCode", "Code d’accès"), form.accessCode, showAccessCode, setShowAccessCode],
                    ["confirmAccessCode", tr("confirmAccessCode", "Confirmer le code d’accès"), form.confirmAccessCode, showAccessCode, setShowAccessCode],
                    ["deletionCode", tr("deletionCode", "Code de suppression"), form.deletionCode, showDeletionCode, setShowDeletionCode],
                    ["confirmDeletionCode", tr("confirmDeletionCode", "Confirmer le code de suppression"), form.confirmDeletionCode, showDeletionCode, setShowDeletionCode],
                  ].map(([name, label, value, visible, toggle]) => (
                    <label key={name} className="block">
                      <span className="mb-2 block text-sm font-bold text-slate-700">{label} *</span>
                      <div className="relative">
                        <LockKeyhole className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                          name={name}
                          type={visible ? "text" : "password"}
                          value={value}
                          onChange={updateField}
                          minLength={6}
                          maxLength={64}
                          required
                          autoComplete="off"
                          className={`${inputClass} pl-11 pr-12`}
                        />
                        <button
                          type="button"
                          onClick={() => toggle((current) => !current)}
                          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs font-semibold leading-5 text-amber-900">
                  {tr("securityCodesNotice", "Le code d’accès sera demandé à chaque connexion Admin. Le code de suppression sera demandé pour supprimer un client, thème, sous-thème, compétence, participant ou certificat.")}
                </div>
              </section>

              <section className="border-t border-slate-100 pt-7">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600 text-white">
                    <FileUp className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">{tr("templateSection", "Modèle de certificat")}</h3>
                    <p className="text-xs text-slate-500">{tr("templateLabel", "PDF vierge de l’organisation")}</p>
                  </div>
                </div>
                <label className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-7 text-center transition ${templateFile ? "border-emerald-300 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:border-blue-300 hover:bg-blue-50"}`}>
                  <input type="file" accept="application/pdf,.pdf" onChange={handleFile} className="sr-only" />
                  {templateFile ? (
                    <>
                      <CheckCircle2 className="h-9 w-9 text-emerald-600" />
                      <p className="mt-3 max-w-full truncate font-black text-emerald-900">{templateFile.name}</p>
                      <p className="mt-1 text-xs font-semibold text-emerald-700">{(templateFile.size / 1024 / 1024).toFixed(2)} Mo · {tr("pdfReady", "PDF prêt à être enregistré")}</p>
                    </>
                  ) : (
                    <>
                      <FileUp className="h-9 w-9 text-blue-600" />
                      <p className="mt-3 font-black text-slate-900">{tr("choosePdf", "Choisir le PDF vierge")}</p>
                    </>
                  )}
                </label>
                <p className="mt-3 text-xs leading-5 text-slate-500">{tr("templateHelp", "PDF obligatoire, 15 Mo maximum.")}</p>

                {templateFile && (
                  <div className="mt-6">
                    <div className="mb-3 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                      <p className="text-sm font-black text-blue-950">
                        {tr("layoutTitle", "Positionnement des données")}
                      </p>
                      <p className="mt-1 text-xs font-semibold leading-5 text-blue-800">
                        {tr(
                          "layoutHelp",
                          "Glissez le nom, la formation, les dates, l’identifiant et le QR code vers leurs emplacements. Ajoutez des zones blanches pour couvrir les textes d’exemple déjà présents."
                        )}
                      </p>
                    </div>
                    <CertificateTemplateDesigner
                      file={templateFile}
                      initialLayout={templateLayout}
                      onChange={handleTemplateLayoutChange}
                      language={lang}
                      organizationName={form.organizationName}
                      defaultFullscreen
                    />
                  </div>
                )}
              </section>

              <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
                {submitting ? tr("creating", "Création en cours...") : tr("create", "Créer mon organisation")}
              </button>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
};

export default OrganizationSetup;
